// SEO landing-page configs for local keyword targeting.
// Each entry generates a fully optimized landing page at `/<slug>` with:
//   • Unique <title>, meta description, keywords
//   • LocalBusiness + Course + FAQ JSON-LD
//   • Keyword-dense H1, intro paragraph, FAQs
// Maintained by Vidya365 marketing.

const PHONE = '+91 85041 10365';

const COMMON_WHY = [
  { title: 'Expert Faculty', desc: '15+ years experience in SSC and Bank exam preparation. Ex-bankers and selected candidates teaching.' },
  { title: 'Proven Results', desc: '500+ final selections across IBPS, SBI, RBI, SSC — verified results with student names.' },
  { title: 'Best Location', desc: 'Centre at Karol Bagh Metro Gate 7 — easy reach from Patel Nagar, Rajendra Place, Connaught Place, Paharganj.' },
  { title: 'Affordable Fees', desc: 'Most affordable coaching in Karol Bagh and Delhi without compromising on quality of teaching or material.' },
  { title: 'Doubt Clearing', desc: 'Daily doubt sessions, 24x7 Guruji AI doubt-bot, plus 1:1 mentor sessions for weak topics.' },
  { title: 'Free Test Series', desc: 'Adda247-style mock tests, sectional tests, previous year papers — included free with every batch.' }
];

const COMMON_FEATURES = [
  { title: 'Live + Recorded Classes', desc: 'Attend offline at Karol Bagh or live online — recordings available anytime for revision.' },
  { title: 'Updated Study Material', desc: 'Latest pattern-based notes, books, e-books and class handouts in English and Hindi medium.' },
  { title: 'Full Mock Test Series', desc: 'Sectional + full-length mocks with detailed solutions, AIR ranking and performance analytics.' },
  { title: 'Previous Year Papers', desc: 'Free PYQ library covering last 10 years of exam — searchable and downloadable.' },
  { title: 'Personal Mentorship', desc: 'Each student gets a dedicated mentor for weekly progress reviews and strategy planning.' },
  { title: 'Interview Guidance', desc: 'Mock interviews with ex-bankers, GD sessions and personality development workshops.' }
];

const cta = (exam) => ({
  ctaHeadline: `Join the Best ${exam} Coaching in Karol Bagh, Delhi`,
  ctaSubtext: `Limited seats for the upcoming batch. Get your free demo class today — call ${PHONE} or enrol online.`
});

export const LOCAL_PAGES = {
  // ============ Aggregated: SSC ============
  'best-ssc-coaching-in-karol-bagh-delhi': {
    examName: 'SSC',
    shortName: 'SSC Coaching Karol Bagh',
    courseName: 'SSC Exam Coaching (CGL, CHSL, MTS, CPO, GD, Stenographer)',
    title: 'Best SSC Coaching in Karol Bagh, Delhi - Vidya365 | SSC CGL, CHSL, MTS, CPO',
    description: 'Best SSC coaching in Karol Bagh, Delhi for SSC CGL, CHSL, MTS, CPO, GD, Stenographer. ⭐4.6 Rated | 500+ Selections | Expert Faculty | Online + Offline classes. Call +91 8504110365.',
    keywords: [
      'best ssc coaching in karol bagh',
      'best ssc coaching in delhi',
      'ssc coaching in karol bagh',
      'ssc coaching in delhi',
      'ssc coaching centre near me',
      'ssc cgl coaching karol bagh',
      'ssc chsl coaching karol bagh',
      'best ssc coaching institute delhi',
      'top ssc coaching delhi',
      'ssc coaching classes near me',
      'Vidya365'
    ],
    h1: 'Best SSC Coaching in Karol Bagh, Delhi',
    subheading: 'Vidya365 — Delhi\'s most trusted SSC coaching institute for CGL, CHSL, MTS, CPO, GD & Stenographer exams.',
    intro: {
      heading: 'Why Vidya365 is the Best SSC Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Looking for the best SSC coaching in Karol Bagh, Delhi? Vidya365 is the top-rated coaching institute in Karol Bagh, just minutes from the metro station, with 500+ verified selections in SSC CGL, SSC CHSL, SSC MTS, SSC CPO and SSC GD exams. Our experienced faculty — including selected SSC candidates and subject experts — deliver classroom and online coaching tailored to the latest exam pattern.',
        'Students from across Delhi NCR — Karol Bagh, Patel Nagar, Rajendra Place, Connaught Place, Paharganj, Pusa Road and surrounding areas — choose Vidya365 for its proven results, affordable fees and 4.6-star Google rating from 263+ verified reviews. Whether you are starting SSC CGL preparation, revising for SSC CHSL Tier 2, or aiming for SSC MTS — we have a batch tailored for you.',
        'Our SSC coaching covers Quantitative Aptitude, Reasoning, English Language, General Awareness and Current Affairs — with daily practice sets, weekly mocks, doubt sessions and personal mentorship. Choose Vidya365 — the most reliable name when you search for "SSC coaching near me" or "best SSC coaching in Delhi".'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 SSC Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best SSC coaching in Karol Bagh?', a: 'Vidya365 is widely rated as the best SSC coaching in Karol Bagh, Delhi with 500+ selections, 4.6★ rating, expert faculty and the most affordable fee structure. We provide coaching for SSC CGL, CHSL, MTS, CPO, GD and Stenographer.' },
      { q: 'Where is the best SSC coaching in Delhi located?', a: 'Vidya365, the best SSC coaching in Delhi, is located at Unit 8, Apsara Arcade, Karol Bagh Metro Gate 7, Pusa Road, New Delhi - 110005. Students from Karol Bagh, Patel Nagar, Rajendra Place and all of Delhi NCR attend our classes.' },
      { q: 'What is the fee for SSC coaching in Karol Bagh?', a: 'Vidya365 offers the most affordable SSC coaching in Karol Bagh and Delhi. Call +91 8504110365 to know current batch fees, available discounts and EMI options.' },
      { q: 'Do you offer online SSC coaching as well?', a: 'Yes — Vidya365 SSC coaching is available in both offline (at our Karol Bagh centre) and online live mode. Recordings are also provided for revision.' },
      { q: 'Is there a free demo class for SSC coaching?', a: 'Yes, Vidya365 offers a free SSC demo class. Book your seat by calling +91 8504110365 or visit the Karol Bagh centre.' }
    ],
    ...cta('SSC')
  },

  // ============ Aggregated: Bank ============
  'best-bank-coaching-in-karol-bagh-delhi': {
    examName: 'Bank',
    shortName: 'Bank Coaching Karol Bagh',
    courseName: 'Bank Exam Coaching (IBPS PO, SBI PO, Clerk, RBI Grade B)',
    title: 'Best Bank Coaching in Karol Bagh, Delhi - Vidya365 | IBPS PO, SBI PO, Clerk',
    description: 'Best bank coaching in Karol Bagh, Delhi for IBPS PO, SBI PO, IBPS Clerk, SBI Clerk, RBI Grade B, NABARD. ⭐4.6 Rated | 500+ Selections | Expert Ex-Banker Faculty. Call +91 8504110365.',
    keywords: [
      'best bank coaching in karol bagh',
      'best bank coaching in delhi',
      'bank coaching in karol bagh',
      'bank coaching in delhi',
      'bank coaching near me',
      'best banking coaching delhi',
      'ibps coaching karol bagh',
      'sbi coaching karol bagh',
      'bank po coaching delhi',
      'bank clerk coaching karol bagh',
      'Vidya365'
    ],
    h1: 'Best Bank Coaching in Karol Bagh, Delhi',
    subheading: 'Vidya365 — top-rated bank exam coaching for IBPS PO, SBI PO, IBPS Clerk, SBI Clerk, RBI Grade B & NABARD.',
    intro: {
      heading: 'Why Vidya365 is the Best Bank Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Vidya365 is recognised as the best bank coaching in Karol Bagh, Delhi with 500+ verified selections across IBPS PO, SBI PO, IBPS Clerk, SBI Clerk, RBI Grade B and NABARD Grade A. Our ex-banker faculty bring real-world banking experience to the classroom, making concepts simple and exam-ready.',
        'Located at Karol Bagh Metro Gate 7, our coaching institute is easily accessible from Patel Nagar, Rajendra Place, Connaught Place, Pusa Road, Paharganj and the rest of Delhi NCR. Whether you are preparing for the IBPS PO Prelims, SBI PO Mains, or the RBI Grade B Phase 2, we offer a structured study plan, daily practice and weekly mocks.',
        'Search results consistently rank Vidya365 as the top bank coaching in Delhi because of our 4.6★ Google rating (263+ reviews), highest success rate (85%), affordable fees and the most comprehensive bank exam syllabus coverage — including Quantitative Aptitude, Reasoning, English, Banking Awareness, General Awareness, and Computer Knowledge.'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 Bank Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best bank coaching in Karol Bagh?', a: 'Vidya365 is the best bank coaching institute in Karol Bagh, Delhi with 500+ selections in IBPS PO, SBI PO, IBPS Clerk, SBI Clerk, RBI Grade B and NABARD. We have a 4.6★ Google rating from 263+ verified student reviews.' },
      { q: 'Which is the best bank coaching in Delhi?', a: 'Vidya365 is widely considered the best bank coaching in Delhi, located in Karol Bagh. Our ex-banker faculty, structured curriculum, daily mocks and interview guidance make us the preferred choice.' },
      { q: 'Do you offer SBI PO and IBPS PO coaching together?', a: 'Yes — Vidya365 offers a combined Bank PO foundation batch covering SBI PO, IBPS PO, IBPS RRB PO, plus a specialised RBI Grade B track.' },
      { q: 'What is the fee for bank coaching at Vidya365?', a: 'Vidya365 offers the most affordable bank coaching in Karol Bagh. Call +91 8504110365 to know current fees, scholarships and EMI options.' },
      { q: 'Are online bank coaching classes available?', a: 'Yes — every bank coaching batch at Vidya365 is available in offline (Karol Bagh) and online live mode with recorded lectures for revision.' }
    ],
    ...cta('Bank')
  },

  // ============ Specific Exams ============
  'best-ssc-cgl-coaching-in-karol-bagh-delhi': {
    examName: 'SSC CGL',
    shortName: 'SSC CGL Coaching',
    courseName: 'SSC CGL Tier 1 + Tier 2 Coaching',
    title: 'Best SSC CGL Coaching in Karol Bagh, Delhi - Vidya365 | Tier 1 + Tier 2',
    description: 'Best SSC CGL coaching in Karol Bagh, Delhi. Tier 1 + Tier 2 complete preparation by expert faculty. ⭐4.6 Rated | 150+ CGL Selections | Online + Offline classes. Call +91 8504110365.',
    keywords: [
      'best ssc cgl coaching in karol bagh',
      'best ssc cgl coaching in delhi',
      'ssc cgl coaching in karol bagh',
      'ssc cgl coaching in delhi',
      'ssc cgl coaching near me',
      'ssc cgl tier 1 coaching delhi',
      'ssc cgl tier 2 coaching delhi',
      'ssc cgl maths coaching karol bagh',
      'ssc cgl english coaching delhi',
      'Vidya365 SSC CGL'
    ],
    h1: 'Best SSC CGL Coaching in Karol Bagh, Delhi',
    subheading: 'Complete SSC CGL Tier 1 + Tier 2 preparation with 150+ confirmed selections and structured study plan.',
    intro: {
      heading: 'Why Vidya365 is the Best SSC CGL Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Vidya365 is the best SSC CGL coaching institute in Karol Bagh, Delhi — with 150+ final SSC CGL selections, dedicated Tier 1 and Tier 2 batches, and faculty who are themselves selected SSC officers. Our coaching covers the full CGL syllabus: Quantitative Aptitude, Reasoning, English Comprehension, General Awareness, plus the Tier 2 specialised papers including Statistics and Finance & Economics.',
        'Students searching for "SSC CGL coaching near me" or "best SSC CGL coaching in Delhi" pick Vidya365 because of our proven track record, 4.6★ rating, smart class infrastructure at Karol Bagh Metro Gate 7, and an exam-pattern-aligned curriculum updated every cycle.'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 SSC CGL Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best SSC CGL coaching in Karol Bagh?', a: 'Vidya365 is the best SSC CGL coaching in Karol Bagh with 150+ verified SSC CGL selections, expert faculty and the most affordable fees. Our centre at Karol Bagh Metro Gate 7 makes us easily accessible from all of Delhi NCR.' },
      { q: 'Which is the best SSC CGL coaching in Delhi?', a: 'Vidya365 ranks among the best SSC CGL coaching institutes in Delhi, located in Karol Bagh. Tier 1 + Tier 2 full preparation with 4.6★ Google rating.' },
      { q: 'How long is the SSC CGL coaching course?', a: 'Vidya365 offers 4-month, 6-month and 12-month SSC CGL coaching batches — pick what fits your exam timeline. Call +91 8504110365 for current batch schedules.' },
      { q: 'Does Vidya365 cover SSC CGL Tier 2 Statistics and Finance papers?', a: 'Yes — our specialised SSC CGL Tier 2 batch covers Statistics, Finance & Economics, and General Studies specialised papers with dedicated subject experts.' }
    ],
    ...cta('SSC CGL')
  },

  'best-ssc-chsl-coaching-in-karol-bagh-delhi': {
    examName: 'SSC CHSL',
    shortName: 'SSC CHSL Coaching',
    courseName: 'SSC CHSL Tier 1 + Tier 2 Coaching',
    title: 'Best SSC CHSL Coaching in Karol Bagh, Delhi - Vidya365 | Tier 1 + Tier 2 + Typing',
    description: 'Best SSC CHSL coaching in Karol Bagh, Delhi for Tier 1, Tier 2 + Typing Test prep. ⭐4.6 Rated | 100+ CHSL Selections | Expert Faculty | Call +91 8504110365.',
    keywords: [
      'best ssc chsl coaching in karol bagh',
      'best ssc chsl coaching in delhi',
      'ssc chsl coaching in karol bagh',
      'ssc chsl coaching in delhi',
      'ssc chsl coaching near me',
      'ssc chsl tier 2 coaching delhi',
      'ssc chsl typing coaching karol bagh',
      'Vidya365 SSC CHSL'
    ],
    h1: 'Best SSC CHSL Coaching in Karol Bagh, Delhi',
    subheading: 'Complete SSC CHSL Tier 1, Tier 2 & Typing Test preparation with 100+ confirmed selections.',
    intro: {
      heading: 'Why Vidya365 is the Best SSC CHSL Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Vidya365 is the best SSC CHSL coaching in Karol Bagh, Delhi with 100+ verified CHSL selections. Our coaching covers Tier 1 (Quantitative Aptitude, English, General Intelligence, General Awareness), Tier 2 descriptive paper, and the crucial CHSL Skill Test / Typing Test — backed by our in-house Free Typing Test platform.',
        'Students searching for "SSC CHSL coaching in Karol Bagh" or "best SSC CHSL coaching in Delhi" choose Vidya365 because of its centre location at Karol Bagh Metro, expert faculty, integrated typing practice, and the most affordable CHSL coaching fees in Delhi.'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 SSC CHSL Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best SSC CHSL coaching in Karol Bagh?', a: 'Vidya365 is the best SSC CHSL coaching in Karol Bagh — 100+ selections, integrated Typing Test platform, expert faculty and the most affordable fees in Delhi.' },
      { q: 'Does the SSC CHSL coaching include typing test practice?', a: 'Yes — Vidya365 SSC CHSL coaching includes unlimited access to our online Typing Test platform with English + Hindi passages, WPM tracking and exam-pattern simulation.' },
      { q: 'Which is the best SSC CHSL coaching in Delhi?', a: 'Vidya365 in Karol Bagh is among the top SSC CHSL coaching institutes in Delhi with proven results and integrated skill-test prep.' }
    ],
    ...cta('SSC CHSL')
  },

  'best-sbi-po-coaching-in-karol-bagh-delhi': {
    examName: 'SBI PO',
    shortName: 'SBI PO Coaching',
    courseName: 'SBI PO Prelims + Mains + Interview Coaching',
    title: 'Best SBI PO Coaching in Karol Bagh, Delhi - Vidya365 | Prelims + Mains + Interview',
    description: 'Best SBI PO coaching in Karol Bagh, Delhi. Prelims + Mains + Interview by ex-banker faculty. ⭐4.6 Rated | 120+ SBI PO Selections | Call +91 8504110365.',
    keywords: [
      'best sbi po coaching in karol bagh',
      'best sbi po coaching in delhi',
      'sbi po coaching in karol bagh',
      'sbi po coaching in delhi',
      'sbi po coaching near me',
      'sbi po prelims coaching delhi',
      'sbi po mains coaching karol bagh',
      'sbi po interview coaching delhi',
      'Vidya365 SBI PO'
    ],
    h1: 'Best SBI PO Coaching in Karol Bagh, Delhi',
    subheading: 'Complete SBI PO Prelims + Mains + Interview preparation by ex-banker faculty with 120+ selections.',
    intro: {
      heading: 'Why Vidya365 is the Best SBI PO Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Vidya365 is the best SBI PO coaching in Karol Bagh, Delhi with 120+ final SBI PO selections. Our ex-SBI banker faculty teach the SBI PO syllabus in depth — Quantitative Aptitude, Reasoning, English Language, Data Analysis & Interpretation, Banking & Economic Awareness, plus the SBI PO Group Exercise and Interview module.',
        'Search "best SBI PO coaching in Delhi" and Vidya365 stands out for its 4.6★ rating, focused descriptive English preparation, mock interview panels with retired bank officers, and centre location at Karol Bagh Metro Gate 7 — easily reachable from Patel Nagar, Rajendra Place, Connaught Place and the rest of Delhi NCR.'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 SBI PO Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best SBI PO coaching in Karol Bagh?', a: 'Vidya365 is the best SBI PO coaching institute in Karol Bagh, Delhi — 120+ SBI PO selections, ex-banker faculty, complete Prelims + Mains + Interview preparation.' },
      { q: 'Which is the best SBI PO coaching in Delhi?', a: 'Vidya365 in Karol Bagh is the top-rated SBI PO coaching in Delhi with proven results, affordable fees and 4.6★ Google rating.' },
      { q: 'Does the SBI PO coaching cover Group Exercise and Interview?', a: 'Yes — Vidya365 SBI PO course includes dedicated Group Exercise & Discussion (GE&D) workshops and full mock interviews with ex-SBI officers.' }
    ],
    ...cta('SBI PO')
  },

  'best-ibps-po-coaching-in-karol-bagh-delhi': {
    examName: 'IBPS PO',
    shortName: 'IBPS PO Coaching',
    courseName: 'IBPS PO Prelims + Mains + Interview Coaching',
    title: 'Best IBPS PO Coaching in Karol Bagh, Delhi - Vidya365 | Prelims + Mains + Interview',
    description: 'Best IBPS PO coaching in Karol Bagh, Delhi. Prelims + Mains + Interview, sectional + full-length mocks. ⭐4.6 Rated | 180+ IBPS Selections | Call +91 8504110365.',
    keywords: [
      'best ibps po coaching in karol bagh',
      'best ibps po coaching in delhi',
      'ibps po coaching in karol bagh',
      'ibps po coaching in delhi',
      'ibps po coaching near me',
      'ibps po prelims coaching delhi',
      'ibps po mains coaching karol bagh',
      'Vidya365 IBPS PO'
    ],
    h1: 'Best IBPS PO Coaching in Karol Bagh, Delhi',
    subheading: 'Complete IBPS PO Prelims + Mains + Interview preparation with 180+ verified selections.',
    intro: {
      heading: 'Why Vidya365 is the Best IBPS PO Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Vidya365 is the best IBPS PO coaching in Karol Bagh, Delhi — known for its 180+ IBPS PO and IBPS RRB PO selections, ex-banker faculty and rigorous mock-test-driven approach. Our coaching covers the full IBPS PO syllabus: Reasoning, Quantitative Aptitude, English, General Awareness, Banking Awareness and Computer Knowledge — plus the Mains-specific Letter/Essay Writing module.',
        'Located at Karol Bagh Metro Gate 7, our IBPS PO classes are attended by students from across Delhi NCR. With 4.6★ rating and the most affordable IBPS PO coaching fees in Delhi, Vidya365 is the consistent top choice for serious bank PO aspirants.'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 IBPS PO Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best IBPS PO coaching in Karol Bagh?', a: 'Vidya365 is the best IBPS PO coaching in Karol Bagh, Delhi with 180+ IBPS PO selections, expert ex-banker faculty and complete Prelims + Mains + Interview preparation.' },
      { q: 'Which is the best IBPS PO coaching in Delhi?', a: 'Vidya365 in Karol Bagh is widely rated as the best IBPS PO coaching in Delhi with proven results and 4.6★ Google rating.' },
      { q: 'Do you also cover IBPS RRB PO?', a: 'Yes — our IBPS PO batch also includes IBPS RRB PO syllabus coverage and a specialised Officer Scale-I module.' }
    ],
    ...cta('IBPS PO')
  },

  'best-rbi-grade-b-coaching-in-karol-bagh-delhi': {
    examName: 'RBI Grade B',
    shortName: 'RBI Grade B Coaching',
    courseName: 'RBI Grade B Phase 1 + Phase 2 + Interview Coaching',
    title: 'Best RBI Grade B Coaching in Karol Bagh, Delhi - Vidya365 | Phase 1 + Phase 2 + Interview',
    description: 'Best RBI Grade B coaching in Karol Bagh, Delhi. Phase 1 + Phase 2 (ESI, FM, English) + Interview by specialised faculty. ⭐4.6 Rated | 50+ RBI Selections | Call +91 8504110365.',
    keywords: [
      'best rbi grade b coaching in karol bagh',
      'best rbi grade b coaching in delhi',
      'rbi grade b coaching in karol bagh',
      'rbi grade b coaching in delhi',
      'rbi grade b coaching near me',
      'rbi grade b phase 2 coaching delhi',
      'rbi grade b esi coaching karol bagh',
      'rbi grade b fm coaching delhi',
      'Vidya365 RBI Grade B'
    ],
    h1: 'Best RBI Grade B Coaching in Karol Bagh, Delhi',
    subheading: 'Specialised Phase 1 + Phase 2 (ESI, FM, English) + Interview coaching with 50+ RBI selections.',
    intro: {
      heading: 'Why Vidya365 is the Best RBI Grade B Coaching in Karol Bagh, Delhi',
      paragraphs: [
        'Vidya365 is the best RBI Grade B coaching in Karol Bagh, Delhi — with 50+ verified RBI Grade B selections. Our specialised faculty cover Phase 1 (General Awareness, English, Quantitative Aptitude, Reasoning) and Phase 2 (Economic & Social Issues, Finance & Management, English Descriptive) in depth, with daily current affairs and weekly mocks.',
        'Searching for "best RBI Grade B coaching in Delhi" or "RBI Grade B coaching near me"? Vidya365 in Karol Bagh is the most preferred choice — 4.6★ rated, with mock interview panels and a dedicated mentor for each RBI aspirant.'
      ]
    },
    whyChoose: { heading: 'Why Choose Vidya365 RBI Grade B Coaching', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best RBI Grade B coaching in Karol Bagh?', a: 'Vidya365 is the best RBI Grade B coaching in Karol Bagh, Delhi with 50+ RBI selections, complete Phase 1 + Phase 2 + Interview prep and a 4.6★ Google rating.' },
      { q: 'Which is the best RBI Grade B coaching in Delhi?', a: 'Vidya365 in Karol Bagh is the top-rated RBI Grade B coaching in Delhi with specialised ESI, FM and English Descriptive faculty.' },
      { q: 'Does the RBI Grade B coaching cover ESI and FM in detail?', a: 'Yes — our RBI Grade B Phase 2 batch covers Economic & Social Issues (ESI) and Finance & Management (FM) with dedicated specialist faculty, daily articles and weekly mocks.' }
    ],
    ...cta('RBI Grade B')
  },

  // ============ Generic Local ============
  'coaching-centre-near-me': {
    examName: 'Government Exam',
    shortName: 'Coaching Centre Near Me',
    courseName: 'Government Exam Coaching (SSC + Bank + RBI)',
    title: 'Coaching Centre Near Me in Karol Bagh, Delhi - Vidya365 | SSC, Bank & RBI',
    description: 'Looking for a coaching centre near me in Karol Bagh, Delhi? Vidya365 — best SSC, Bank & RBI Grade B coaching classes near you. ⭐4.6 Rated | 500+ Selections | Call +91 8504110365.',
    keywords: [
      'coaching centre near me',
      'coaching classes near me',
      'best coaching centre near me karol bagh',
      'coaching institute near me delhi',
      'ssc coaching near me',
      'bank coaching near me',
      'rbi coaching near me',
      'government exam coaching near me',
      'Vidya365 Karol Bagh'
    ],
    h1: 'Best Coaching Centre Near Me — Karol Bagh, Delhi',
    subheading: 'Search "coaching centre near me" or "coaching classes near me"? Vidya365 in Karol Bagh is Delhi\'s most trusted SSC, Bank & RBI Grade B coaching institute.',
    intro: {
      heading: 'Vidya365 — The Coaching Centre Near You in Karol Bagh, Delhi',
      paragraphs: [
        'If you are searching "coaching centre near me" or "coaching classes near me" while in Karol Bagh, Patel Nagar, Rajendra Place, Connaught Place, Pusa Road or anywhere in Delhi NCR — Vidya365 is the answer. Our coaching centre is located at Unit 8, Apsara Arcade, Karol Bagh Metro Gate 7 — easy 5-minute reach from the metro and accessible from every major part of Delhi.',
        'Vidya365 is the best coaching centre near you for SSC CGL, SSC CHSL, IBPS PO, SBI PO, IBPS Clerk, SBI Clerk, RBI Grade B and NABARD Grade A — with 500+ verified selections, 4.6★ Google rating from 263+ reviews, expert faculty, and the most affordable fees in Delhi.'
      ]
    },
    whyChoose: { heading: 'Why Vidya365 is the Best Coaching Centre Near You', points: COMMON_WHY },
    features: COMMON_FEATURES,
    faqs: [
      { q: 'Which is the best coaching centre near me in Karol Bagh?', a: 'Vidya365 is the best coaching centre near you in Karol Bagh, Delhi — located at Karol Bagh Metro Gate 7, with SSC, Bank and RBI Grade B coaching and 500+ verified selections.' },
      { q: 'Are there coaching classes near me for SSC and Bank exams?', a: 'Yes — Vidya365 offers SSC and Bank coaching classes in Karol Bagh, easily accessible from Patel Nagar, Rajendra Place, Connaught Place and all of Delhi NCR. Both offline and online live classes are available.' },
      { q: 'How do I reach the Vidya365 coaching centre in Karol Bagh?', a: 'The Vidya365 coaching centre is at Unit 8, Apsara Arcade, Near Karol Bagh Metro Gate 7, Pusa Road, New Delhi - 110005. The nearest metro is Karol Bagh (Blue Line). Call +91 8504110365 for directions.' }
    ],
    ...cta('Government Exam')
  }
};

// Convenient slug list for sitemap and internal linking
export const LOCAL_PAGE_SLUGS = Object.keys(LOCAL_PAGES);
